// 浏览器版 ElectronApi 垫片：仅实现 ControllerSession 实际用到的方法。
// 剪贴板走 navigator.clipboard（需 https/localhost 安全上下文）；
// 非安全上下文下剪贴板受限，由 app.ts 提供「文本发送」降级路径。

import { DEFAULT_CLIENT_PORT, getCurrentServerUrl } from '../shared/constants';
import { DEFAULT_REMOTE_STREAM_SETTINGS } from '../shared/stream-profile';
import { MAX_CLIPBOARD_LENGTH } from '../shared/datachannel';
import type {
  AuthSession,
  AuthSessionsResult,
  RemoteDevice,
  SecurityActivityResult,
} from '../shared/electron-api';
import {
  MAX_CLIPBOARD_IMAGE_BYTES,
  normalizeClipboardImageMimeType,
  type ClipboardFileData,
} from '../shared/clipboard-file';
import { MAX_CLIPBOARD_HTML_LENGTH, sanitizeClipboardHtml } from '../shared/clipboard-html';
import type { RemoteFileTransferTask } from '../shared/file-transfer-tasks';

type RefreshAuthResult = { ok: boolean; username?: string; error?: string };

export class MobileApi {
  // 连接上下文（由 app.ts 在登录/直连时写入）
  serverUrl = getCurrentServerUrl();
  token = '';
  refreshToken = '';
  username = '';
  private localDeviceId: string | null = null;
  private localDeviceIdCheckedAt = 0;
  private localDeviceProbe: Promise<string | null> | null = null;
  // 页面恢复、设备轮询和控制会话可能同时发现令牌过期。只允许一个
  // refresh 请求消费当前刷新令牌，其他调用共享同一个结果。
  private refreshInFlight: Promise<RefreshAuthResult> | null = null;

  async getSettings(): Promise<Record<string, unknown>> {
    return {
      serverUrl: this.serverUrl,
      token: this.token,
      refreshToken: this.refreshToken,
      username: this.username,
      deviceName: 'mobile',
      lanPasscodeHash: null,
      muteAudio: false,
      streamProfile: 'balanced',
      remoteStreamSettings: { ...DEFAULT_REMOTE_STREAM_SETTINGS },
      theme: 'dark',
    };
  }

  async getLocalDeviceId(): Promise<string | null> {
    // HTTPS 页面不能直接访问本机的 ws:// 服务，失败时由列表继续按普通远端设备展示。
    if (location.protocol === 'https:') return null;
    if (this.localDeviceIdCheckedAt && Date.now() - this.localDeviceIdCheckedAt < 15_000) {
      return this.localDeviceId;
    }
    if (this.localDeviceProbe) return this.localDeviceProbe;

    this.localDeviceProbe = new Promise<string | null>((resolve) => {
      let settled = false;
      const socket = new WebSocket(`ws://127.0.0.1:${DEFAULT_CLIENT_PORT}/ws`);
      const timeout = window.setTimeout(() => finish(null), 900);
      const finish = (value: string | null): void => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timeout);
        this.localDeviceId = value;
        this.localDeviceIdCheckedAt = Date.now();
        try {
          if (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING) {
            socket.close();
          }
        } catch {
          // ignore
        }
        resolve(value);
      };
      socket.onopen = () => {
        try {
          socket.send(JSON.stringify({ type: 'device-info' }));
        } catch {
          finish(null);
        }
      };
      socket.onmessage = (event) => {
        try {
          const message = JSON.parse(String(event.data)) as { type?: string; deviceId?: string };
          finish(message.type === 'device-info' && message.deviceId ? message.deviceId : null);
        } catch {
          finish(null);
        }
      };
      socket.onerror = () => finish(null);
      socket.onclose = () => finish(null);
    }).catch(() => null).finally(() => {
      this.localDeviceProbe = null;
    });
    return this.localDeviceProbe;
  }

  async getIceServers(): Promise<{ iceServers: unknown[] }> {
    try {
      const { response: r, data } = await this.requestJson<{ iceServers?: unknown[] }>('/api/ice-servers');
      if (!r.ok) throw new Error('ice-servers ' + r.status);
      return { iceServers: Array.isArray(data.iceServers) ? data.iceServers : [] };
    } catch {
      // 拉取失败回退默认 STUN（ControllerSession 内部也有兜底）
      return { iceServers: [] };
    }
  }

  async refreshAuth(signal?: AbortSignal): Promise<RefreshAuthResult> {
    if (this.refreshInFlight) return this.refreshInFlight;
    const request = (async (): Promise<RefreshAuthResult> => {
      if (!this.refreshToken) return { ok: false, error: '登录已过期，请重新登录' };
      try {
        const response = await fetch(`${this.serverUrl}/api/auth/refresh`, {
          method: 'POST',
          headers: { 'content-type': 'application/json', 'X-LinkDesk-Client': 'web' },
          body: JSON.stringify({ refreshToken: this.refreshToken }),
          signal,
        });
        const data = await response.json().catch(() => ({})) as {
          token?: string; refreshToken?: string; username?: string; error?: { message?: string };
        };
        if (!response.ok || typeof data.token !== 'string' || typeof data.refreshToken !== 'string') {
          return { ok: false, error: data.error?.message || '登录已过期，请重新登录' };
        }
        this.token = data.token;
        this.refreshToken = data.refreshToken;
        if (data.username) this.username = data.username;
        return { ok: true, username: this.username };
      } catch {
        return { ok: false, error: '暂时无法连接，请稍后重试' };
      }
    })();
    this.refreshInFlight = request;
    try {
      return await request;
    } finally {
      if (this.refreshInFlight === request) this.refreshInFlight = null;
    }
  }

  async authMe(signal?: AbortSignal): Promise<{ ok: boolean; username?: string; token?: string; error?: string; unauthorized?: boolean }> {
    if (!this.token) return { ok: false, unauthorized: true, error: '未登录' };
    try {
      const { response, data } = await this.requestJson<{ username?: string; token?: string; error?: { message?: string } }>('/api/me', { signal });
      if (response.ok && typeof data.username === 'string') {
        if (data.token) this.token = data.token;
        if (data.username) this.username = data.username;
        return { ok: true, username: this.username, ...(data.token ? { token: data.token } : {}) };
      }
      return { ok: false, unauthorized: response.status === 401, error: data.error?.message || '登录状态暂时无法确认' };
    } catch {
      return { ok: false, error: '暂时无法连接，请稍后重试' };
    }
  }

  async disconnectDevice(deviceId: string): Promise<{ ok: boolean; error?: string }> {
    try {
      const { response, data } = await this.requestJson<{ error?: { message?: string } }>(`/api/devices/${encodeURIComponent(deviceId)}/disconnect`, {
        method: 'POST',
      });
      return response.ok
        ? { ok: true }
        : { ok: false, error: data?.error?.message || '结束连接失败' };
    } catch {
      return { ok: false, error: '暂时无法连接，请稍后重试' };
    }
  }

  async unbindDevice(deviceId: string): Promise<{ ok: boolean; error?: string }> {
    if (typeof deviceId !== 'string' || !/^[A-Za-z0-9._:-]{1,128}$/.test(deviceId)) {
      return { ok: false, error: '设备信息无效' };
    }
    try {
      const { response, data } = await this.requestJson<{ error?: { message?: string } }>(`/api/devices/${encodeURIComponent(deviceId)}`, {
        method: 'DELETE',
      });
      return response.ok ? { ok: true } : { ok: false, error: data?.error?.message || '取消关联失败' };
    } catch {
      return { ok: false, error: '暂时无法连接，请稍后重试' };
    }
  }

  async deleteAccount(password: string, confirmation: string): Promise<{ ok: boolean; error?: string }> {
    if (typeof password !== 'string' || password.length < 1 || password.length > 128 ||
        typeof confirmation !== 'string' || confirmation.trim().length < 1 || confirmation.length > 128) {
      return { ok: false, error: '请填写当前密码，并准确输入账号名称' };
    }
    try {
      const { response, data } = await this.requestJson<{ error?: { message?: string } }>('/api/auth/delete-account', {
        method: 'POST',
        headers: { 'content-type': 'application/json', 'X-LinkDesk-Client': 'web' },
        body: JSON.stringify({ password, confirmation }),
      });
      return response.ok ? { ok: true } : { ok: false, error: data?.error?.message || '账号删除失败' };
    } catch {
      return { ok: false, error: '暂时无法连接，请稍后重试' };
    }
  }

  async listDevices(signal?: AbortSignal): Promise<{ ok: boolean; error?: string; unauthorized?: boolean; devices: RemoteDevice[] }> {
    if (!this.token) return { ok: false, unauthorized: true, error: '未登录', devices: [] };
    try {
      const { response, data } = await this.requestJson<RemoteDevice[] | { error?: { message?: string } }>('/api/devices', { signal });
      if (response.ok && Array.isArray(data)) return { ok: true, devices: data };
      const detail = !Array.isArray(data) ? data.error?.message : undefined;
      return { ok: false, unauthorized: response.status === 401, error: detail || '获取设备列表失败', devices: [] };
    } catch {
      return { ok: false, error: '暂时无法连接，请稍后重试', devices: [] };
    }
  }

  async listFileTransferTasks(signal?: AbortSignal): Promise<{
    ok: boolean;
    error?: string;
    unauthorized?: boolean;
    tasks: RemoteFileTransferTask[];
  }> {
    if (!this.token) return { ok: false, unauthorized: true, error: '未登录', tasks: [] };
    try {
      const { response, data } = await this.requestJson<{
        tasks?: RemoteFileTransferTask[];
        error?: { message?: string };
      }>('/api/file-tasks', { signal });
      if (response.ok && Array.isArray(data.tasks)) return { ok: true, tasks: data.tasks };
      return {
        ok: false,
        unauthorized: response.status === 401,
        error: data.error?.message || '传输任务暂时无法加载',
        tasks: [],
      };
    } catch {
      return { ok: false, error: '暂时无法连接，请稍后重试', tasks: [] };
    }
  }

  async upsertFileTransferTask(task: Omit<RemoteFileTransferTask, 'createdAt' | 'updatedAt' | 'state'> & {
    state: RemoteFileTransferTask['state'];
  }): Promise<{ ok: boolean; error?: string; unauthorized?: boolean }> {
    if (!this.token) return { ok: false, unauthorized: true, error: '未登录' };
    try {
      const { response, data } = await this.requestJson<{ error?: { message?: string } }>(
        `/api/file-tasks/${encodeURIComponent(task.id)}`,
        {
          method: 'PUT',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(task),
        },
      );
      return response.ok
        ? { ok: true }
        : { ok: false, unauthorized: response.status === 401, error: data.error?.message || '传输任务暂时无法保存' };
    } catch {
      return { ok: false, error: '暂时无法连接，请稍后重试' };
    }
  }

  async deleteFileTransferTask(id: string): Promise<{ ok: boolean; error?: string; unauthorized?: boolean }> {
    if (!this.token) return { ok: false, unauthorized: true, error: '未登录' };
    if (!/^[A-Za-z0-9._:-]{1,128}$/.test(id)) return { ok: false, error: '任务信息无效' };
    try {
      const { response, data } = await this.requestJson<{ error?: { message?: string } }>(
        `/api/file-tasks/${encodeURIComponent(id)}`,
        { method: 'DELETE' },
      );
      return response.ok || response.status === 404
        ? { ok: true }
        : { ok: false, unauthorized: response.status === 401, error: data.error?.message || '传输任务暂时无法清理' };
    } catch {
      return { ok: false, error: '暂时无法连接，请稍后重试' };
    }
  }

  async logout(): Promise<void> {
    if (!this.token) return;
    try {
      if (this.refreshToken) await this.refreshAuth();
      await fetch(`${this.serverUrl}/api/auth/logout`, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${this.token}`,
          'X-LinkDesk-Client': 'web',
        },
      });
    } catch {
      // 本地退出不能被网络状态阻塞；服务端会在令牌自然过期后清理旧会话。
    }
  }

  async listAuthSessions(): Promise<AuthSessionsResult> {
    if (!this.token) return { ok: false, unauthorized: true, error: '未登录', sessions: [] };
    try {
      const { response, data } = await this.requestJson<{ sessions?: AuthSession[]; error?: { message?: string } }>('/api/auth/sessions', {
        headers: { 'X-LinkDesk-Client': 'web' },
      });
      if (response.ok && Array.isArray(data.sessions)) return { ok: true, sessions: data.sessions };
      return {
        ok: false,
        unauthorized: response.status === 401,
        error: data.error?.message || '登录设备暂时无法加载',
        sessions: [],
      };
    } catch {
      return { ok: false, error: '暂时无法连接，请稍后重试', sessions: [] };
    }
  }

  async listSecurityActivity(signal?: AbortSignal): Promise<SecurityActivityResult> {
    if (!this.token) return { ok: false, unauthorized: true, error: '未登录', events: [] };
    try {
      const { response, data } = await this.requestJson<{
        events?: SecurityActivityResult['events'];
        error?: { message?: string };
      }>('/api/auth/activity', {
        headers: { 'X-LinkDesk-Client': 'web' },
        signal,
      });
      if (response.ok && Array.isArray(data.events)) {
        return { ok: true, events: data.events };
      }
      return {
        ok: false,
        unauthorized: response.status === 401,
        error: data.error?.message || '最近活动暂时无法加载',
        events: [],
      };
    } catch {
      return { ok: false, error: '暂时无法连接，请稍后重试', events: [] };
    }
  }

  async revokeAuthSession(id: string): Promise<{ ok: boolean; error?: string }> {
    if (!/^[a-f0-9]{16,128}$/.test(id)) return { ok: false, error: '会话信息无效' };
    try {
      const { response, data } = await this.requestJson<{ error?: { message?: string } }>(`/api/auth/sessions/${encodeURIComponent(id)}`, {
        method: 'DELETE',
      });
      return response.ok ? { ok: true } : { ok: false, error: data?.error?.message || '退出设备失败' };
    } catch {
      return { ok: false, error: '暂时无法连接，请稍后重试' };
    }
  }

  async revokeOtherAuthSessions(): Promise<{ ok: boolean; error?: string; revoked?: number }> {
    try {
      const { response, data } = await this.requestJson<{ revoked?: number; error?: { message?: string } }>('/api/auth/sessions/revoke-others', {
        method: 'POST',
      });
      return response.ok
        ? { ok: true, revoked: Number(data?.revoked || 0) }
        : { ok: false, error: data?.error?.message || '退出其他设备失败' };
    } catch {
      return { ok: false, error: '暂时无法连接，请稍后重试' };
    }
  }

  async readClipboard(): Promise<string> {
    try {
      if (navigator.clipboard && navigator.clipboard.readText) {
        return await navigator.clipboard.readText();
      }
    } catch {
      /* 非安全上下文或拒绝，返回空 */
    }
    return '';
  }

  async readClipboardFiles(): Promise<ClipboardFileData[]> {
    // 图片只能在用户点击“粘贴”按钮时由浏览器读取；ControllerSession
    // 会先调用这里，再回退到文本剪贴板。没有权限或浏览器不支持时，
    // 返回空数组不会改变原有文本粘贴路径。
    try {
      if (!navigator.clipboard || typeof navigator.clipboard.read !== 'function') return [];
      const items = await navigator.clipboard.read();
      for (const item of items) {
        const mimeType = item.types
          .map((type) => normalizeClipboardImageMimeType(type))
          .find((type): type is string => type !== null);
        if (!mimeType) continue;
        const blob = await item.getType(mimeType);
        if (!blob.size || blob.size > MAX_CLIPBOARD_IMAGE_BYTES) continue;
        const data = await blob.arrayBuffer();
        const extension = mimeType === 'image/jpeg'
          ? 'jpg'
          : mimeType.slice('image/'.length);
        return [{
          name: `image.${extension}`,
          size: data.byteLength,
          kind: 'image',
          mimeType,
          data,
        }];
      }
    } catch {
      // 浏览器权限、非安全上下文或系统剪贴板暂时不可用时，交给文本路径处理。
    }
    return [];
  }

  async readClipboardHtml(): Promise<string> {
    try {
      if (!navigator.clipboard || typeof navigator.clipboard.read !== 'function') return '';
      const items = await navigator.clipboard.read();
      for (const item of items) {
        if (!item.types.includes('text/html')) continue;
        const html = await (await item.getType('text/html')).text();
        return sanitizeClipboardHtml(html);
      }
    } catch {
      // 浏览器权限或非安全上下文不允许读取时，调用方继续使用纯文本。
    }
    return '';
  }

  async writeClipboardImage(data: ArrayBuffer, mimeType: string): Promise<boolean> {
    const normalizedMimeType = normalizeClipboardImageMimeType(mimeType);
    try {
      if (!normalizedMimeType || !navigator.clipboard || typeof navigator.clipboard.write !== 'function') {
        return false;
      }
      if (data.byteLength <= 0 || data.byteLength > MAX_CLIPBOARD_IMAGE_BYTES || typeof ClipboardItem === 'undefined') {
        return false;
      }
      const item = new ClipboardItem({
        [normalizedMimeType]: new Blob([data], { type: normalizedMimeType }),
      });
      await navigator.clipboard.write([item]);
      return true;
    } catch {
      // 自动写入通常不在用户手势内；文件接收层会回退为下载图片。
      return false;
    }
  }

  async writeClipboardHtml(text: string, html: string, _rtf?: string): Promise<boolean> {
    if (typeof text !== 'string' || text.length > MAX_CLIPBOARD_LENGTH ||
        typeof html !== 'string' || html.length > MAX_CLIPBOARD_HTML_LENGTH) return false;
    const safeHtml = sanitizeClipboardHtml(html);
    if (!safeHtml || !navigator.clipboard || typeof navigator.clipboard.write !== 'function' || typeof ClipboardItem === 'undefined') {
      return this.writeClipboard(text);
    }
    try {
      const item = new ClipboardItem({
        'text/plain': new Blob([text], { type: 'text/plain' }),
        'text/html': new Blob([safeHtml], { type: 'text/html' }),
      });
      await navigator.clipboard.write([item]);
      return true;
    } catch {
      // 自动写入不在用户手势内时，回退为纯文本写入。
      return this.writeClipboard(text);
    }
  }

  async writeClipboard(text: string): Promise<boolean> {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch {
      // 非安全上下文或浏览器策略可能拒绝异步剪贴板；继续尝试旧式同步复制。
    }

    // 兼容通过 HTTP 访问的内网 Web 页面。此降级不打开弹层，也不抢焦点。
    const active = document.activeElement as HTMLElement | null;
    const input = document.createElement('textarea');
    input.value = text;
    input.setAttribute('readonly', 'true');
    input.style.position = 'fixed';
    input.style.left = '-9999px';
    input.style.top = '0';
    input.style.opacity = '0';
    input.style.pointerEvents = 'none';
    document.body.appendChild(input);
    input.focus({ preventScroll: true });
    input.select();
    let copied = false;
    try {
      copied = document.execCommand('copy');
    } catch {
      copied = false;
    }
    input.remove();
    // 旧式复制需要临时选中隐藏文本框，但不能让键盘控制焦点停在这里。
    if (active && active !== document.body) {
      try {
        active.focus({ preventScroll: true });
      } catch {
        // 当前元素可能已被页面卸载，忽略焦点恢复失败。
      }
    }
    return copied;
  }

  async saveFile(name: string, data: ArrayBuffer): Promise<void> {
    const blob = new Blob([data]);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  }

  private async requestJson<T = Record<string, unknown>>(
    apiPath: string,
    init: RequestInit = {},
  ): Promise<{ response: Response; data: T }> {
    const send = async (): Promise<{ response: Response; data: T }> => {
      const headers = new Headers(init.headers || {});
      if (this.token) headers.set('authorization', `Bearer ${this.token}`);
      const response = await fetch(`${this.serverUrl}${apiPath}`, {
        ...init,
        headers,
      });
      const data = await response.json().catch(() => ({})) as T;
      return { response, data };
    };

    let result = await send();
    // 访问令牌刚好过期时，先轮换刷新令牌，再重试一次原请求。刷新接口
    // 本身不经过此分支，因此不会递归；重试次数固定为一次，避免死循环。
    if (result.response.status === 401 && this.refreshToken && apiPath !== '/api/auth/refresh') {
      const refreshed = await this.refreshAuth(init.signal || undefined);
      if (refreshed.ok) result = await send();
    }
    return result;
  }
}
